<template>
  <!-- Preview Mode -->
  <div 
    v-if="showPreview && !isFullscreen"
    class="map-preview" 
    @click="handleExpand"
  >
    <LoadingOverlay 
      :show="loading"
      :error="error"
      :text="`${$t('Loading')} ${layerInfo}...`"
      :error-text="$t('Failed to load layer')"
    />
  
    <div ref="previewContainer" class="preview-map-container"></div>
   
    <div class="map-overlay">
      <button class="expand-btn" @click.stop="handleExpand">
        <q-icon name="keyboard_double_arrow_right" size="1rem" />
        {{ $t('Expand Preview') }}
        <q-icon name="keyboard_double_arrow_left" size="1rem" />
      </button>
    </div>

    <QueryPanel 
      ref="queryComponentPreview"
      :map="previewMap" 
      :is-compact="true"
      :projection="projection"
      :cached-result="cachedQuery.result"
      @query-submitted="handleQueryResult"
    />
  </div>

  <Teleport to="body">
    <div 
      v-if="isFullscreen" 
      class="fullscreen-overlay"
      @click.self="handleClose"
    >
    
      <div class="fullscreen-container">
        <!-- Header -->
        <div class="fullscreen-header">
          <div class="header-left">
            <q-btn 
              flat 
              round 
              icon="arrow_back" 
              @click="handleClose"
            >
              <q-tooltip>{{ $t('Close') }}</q-tooltip>
            </q-btn>
            <span class="header-title" v-html="layerTitle"/>
          </div>
          
          <div class="header-right">
            <MapSearch 
              :map="map"
              :projection="projection"
              @location-selected="handleLocationSelected"
              @search-opened="handleSearchOpened"
              @search-closed="handleSearchClosed"
            />
          </div>
        </div>

        <!-- Layer Menu Toggle -->
        <div class="map-controls-overlay">
          <button 
            class="toggle-btn" 
            :class="{ 'active': layerMenuOpen }"
            :title="$t('Layers')"
            @click.stop="toggleLayerMenu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" stroke-width="2"/>
              <path d="M2 17l10 5 10-5" stroke="currentColor" stroke-width="2"/>
              <path d="M2 12l10 5 10-5" stroke="currentColor" stroke-width="2"/>
            </svg>
          </button>
        </div>
         
        <!-- Map Container -->
        <div class="fullscreen-map-container">
          <div ref="fullscreenContainer" class="fullscreen-map"></div>

          <MapLegend 
            v-if="showLegend"
            :active-panel="activePanel"
            :legend-data="legendData" 
            :layer-info="legendLayerInfo"
            :show-numbers="false"
            position="center"
            @item-selected="handleLegendItemSelected"
            @legend-toggled="handleLegendToggled"
          />

          <TimeSlider
            :active-panel="activePanel"
            v-model="currentYear"
            :available-years="availableYears"
            position="bottom-center"
            @year-changed="handleYearChange"
            @panel-opened="setActivePanel('time')"
          />

          <MapControls 
            v-if="map" 
            :map="map"
            :projection="projection"
            :default-center="homeCoordinate"
            :default-zoom="15"
            @reset-to-home="highlightHomeField"
          />
           
          <LoadingOverlay 
            :show="loading"
            :error="error"
            :text="`${$t('Loading')} ${layerInfo}...`"
            :error-text="$t('Failed to load layer')"
            show-retry
            @retry="retryLoad"
          />
        </div>

        <div class="panels-container">
          <!-- Layer Menu -->
          <LayerMenu
            :is-open="layerMenuOpen"
            :selected-layer-id="layerId"
            :selected-base-layer="selectedBaseLayer"
            :layer-opacity="currentOpacity"
            :data-layers="dataLayers"
            :base-layers="baseLayers"
            @layer-changed="handleLayerChange"
            @base-layer-changed="handleBaseLayerChange"
            @opacity-changed="handleOpacityChange"
            @projection-changed="handleProjectionChange"
            @field-boundaries-toggled="handleFieldBoundariesToggle"
            @close="layerMenuOpen = false; activePanel = null"
          />

          <!-- Query Panel -->
          <QueryPanel 
            ref="queryComponentFullscreen"
            :active-panel="activePanel"
            :map="map" 
            :projection="projection"
            :cached-result="cachedQuery.result"
            @query-submitted="handleQueryResult"
            @panel-opened="setActivePanel('query')"
          />
        </div>
      </div> 
    </div>
  </Teleport>

  <FieldInfo
    :is-open="showFieldPopup"
    :field-data="selectedFieldData"
    :coordinate="selectedFieldCoordinate"
    @close="closeFieldPopup"
    @set-as-home="handleSetAsHome"
  />
</template>

<script>
import 'ol/ol.css'
import { Map, View } from 'ol'
import TileLayer from 'ol/layer/Tile'
import WebGLTile from 'ol/layer/WebGLTile.js'
import OSM from 'ol/source/OSM'
import { GeoTIFF as GeoTIFFSource } from 'ol/source'
import { fromLonLat, transformExtent, transform } from 'ol/proj'
import { get as getProjection } from 'ol/proj'
import { Attribution } from 'ol/control'
import VectorLayer from 'ol/layer/Vector'
import WebGLVectorLayer from 'ol/layer/WebGLVector'
import VectorSource from 'ol/source/Vector'
import GeoJSON from 'ol/format/GeoJSON'
import { Style, Fill, Stroke } from 'ol/style'
import { Select } from 'ol/interaction'
import { singleClick } from 'ol/events/condition'
import proj4 from 'proj4'
import { register } from 'ol/proj/proj4.js'
import MapSearch from './map-search.vue'
import MapControls from './map-controls.vue'
import MapLegend from './map-legend.vue'
import LayerMenu from './layer-menu.vue'
import TimeSlider from './time-slider.vue'
import QueryPanel from './query-panel.vue'
import FieldInfo from './field-info.vue'
import LoadingOverlay from '@/common/components/ui/loading-overlay.vue'
import XYZ from 'ol/source/XYZ'
import { symOutlinedNotificationMultiple } from '@quasar/extras/material-symbols-outlined'

const _0xA1 = { _c: [15.9819, 45.8150], _z: 11, _o: 0.7, _nd: -9999, _mz: 13, _mt: 2048 }
const _0xA2 = {
  _s: 'EPSG:4258', _p: 'EPSG:3035', _w: 'EPSG:4326', _m: 'EPSG:3857',
  _s4: '+proj=longlat +ellps=GRS80 +no_defs +type=crs',
  _p3: '+proj=laea +lat_0=52 +lon_0=10 +x_0=4321000 +y_0=3210000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs'
}
const _0xA3 = {
  _osm: 'osm', _sat: 'satellite', _fl: 'fieldLayer',
  _ti: 'DataCube/GetTitles', _sf: 'DataCube/GetShdcFiles', _fb: 'User/GetFieldBoundaries'
}
const _0xB1 = (h, a = 1) => { const r = parseInt(h.slice(1,3),16), g = parseInt(h.slice(3,5),16), b = parseInt(h.slice(5,7),16); return `rgba(${r},${g},${b},${a})` }
const _0xB2 = (p) => { const c = typeof p === 'string' ? p : p.getCode(); return c.split(':')[1] }
const _0xB3 = (rgba) => {
  const [r,g,b,a=1] = rgba; const n = r<=1&&g<=1&&b<=1
  const [rv,gv,bv] = n ? [Math.round(r*255),Math.round(g*255),Math.round(b*255)] : [Math.round(r),Math.round(g),Math.round(b)]
  const h = v => Math.max(0,Math.min(255,v)).toString(16).padStart(2,'0')
  return `#${h(rv)}${h(gv)}${h(bv)}`
}
const _0xB4 = (f,t) => t === undefined ? `${f.toFixed(1)}+` : `${f.toFixed(1)} - ${t.toFixed(1)}`
const _0xB5 = () => [
  { color_code:'#0000ff',text:'Very Low (0-10)',from:0,to:10,active:true },
  { color_code:'#00ffff',text:'Low (10-20)',from:10,to:20,active:true },
  { color_code:'#ffff00',text:'Medium (20-35)',from:20,to:35,active:true },
  { color_code:'#ff8000',text:'High (35-45)',from:35,to:45,active:true },
  { color_code:'#ff0000',text:'Very High (45+)',from:45,to:50,active:true }
]
const _0xB6 = () => ({color:['case',['==',['band',1],255],[0,0,0,0],['==',['band',1],-9999],[0,0,0,0],['<=',['band',1],0],[0,0,0,0],['interpolate',['linear'],['band',1],1,[0,0,255,0.8],15,[0,255,255,0.8],25,[255,255,0,0.8],35,[255,128,0,0.8],50,[255,0,0,0.8]]]})
const _0xB7 = (f) => { if(f.label){const m=f.label.match(/\d{4}(?=\d{4}$)/);if(m){const y=parseInt(m[0]);if(y>=1990&&y<=new Date().getFullYear()+10)return y}} return null }
const _0xB8 = (c) => { const v=parseFloat(c);if(typeof c==='string'&&['h','m','l','c','p',''].includes(c))return c;if(v>=0.8)return'h';if(v>=0.6)return'm';return'l' }
const _0xB9 = () => { try{const cv=document.createElement('canvas'),gl=cv.getContext('webgl')||cv.getContext('experimental-webgl');if(!gl)return false;return gl.getParameter(gl.MAX_TEXTURE_SIZE)>=_0xA1._mt}catch(e){return false} }

export default {
  name: 'Map',
  components: { MapSearch, MapControls, MapLegend, LayerMenu, TimeSlider, QueryPanel, FieldInfo, LoadingOverlay },
  props: {
    layerId: { type: [String, Number], default: 52 },
    layerDepth: { type: String, default: '0-20' },
    layerConfidence: { type: [String, Number], default: 'm' },
    opacity: { type: Number, default: _0xA1._o },
    center: { type: Array, default: () => [..._0xA1._c] },
    zoom: { type: Number, default: _0xA1._z },
    showPreview: { type: Boolean, default: true }
  },

  emits: ['expand','close','map-ready','layerChanged','layer-loaded','layer-error','update:layerId'],

  data() {
    return {
      useWebGL: _0xB9(),
      cachedQuery: { result: null, title: null, coords: null },
      queryHome: null,
      queryHomeActive: false,
      loading: false,
      error: false,
      layerTitle: '',
      map: null,
      fullscreenMap: null,
      currentDataLayer: null,
      fullscreenDataLayer: null,
      projection: null,
      mapLoaded: false,
      isFullscreen: false,
      selectedLayerId: 52,
      selectedBaseLayer: _0xA3._osm,
      showLegend: true,
      homeCoordinate: [..._0xA1._c],
      homeData: { coordinate: [..._0xA1._c], fieldData: null },
      homeLoading: false,
      lastSelectedFeature: null,
      legendExpanded: false,
      selectedLegendIndex: null,
      legendData: null,
      layerMenuOpen: false,
      currentOpacity: this.opacity ?? _0xA1._o,
      dataLayers: null,
      baseLayers: [
        { id: _0xA3._osm, name: 'Street Map', category: 'Street' },
        { id: _0xA3._sat, name: 'Satellite', category: 'Satellite' }
      ],
      fieldLayer: null,
      fieldLoading: false,
      fieldBoundariesSource: null,
      fieldBoundariesLayer: null,
      fieldBoundariesFormat: new GeoJSON(),
      availableYears: [],
      currentYear: null,
      allLayerFiles: [],
      layerMetadata: null,
      showFieldPopup: false,
      selectedFieldData: null,
      selectedFieldCoordinate: null,
      fieldBoundariesVisible: true,
      currentProjection: _0xA2._p,
      activePanel: null
    }
  },

  watch: {
    '$q.dark.isActive'(_0x1) {
      const _0x2 = (m) => { if(!m)return;const bl=m.getLayers().getArray()[0];if(bl)bl.setOpacity(_0x1?0.6:1.0) }
      _0x2(this.previewMap); _0x2(this.map)
    },
    layerId: {
      handler(_0x3, _0x4) {
        if(_0x3!==_0x4){
          this.availableYears=[];this.currentYear=null;this.allLayerFiles=[];this.layerMetadata=null
          if(this.previewMap)this.loadLayer(_0x3)
          if(this.map)this.loadLayer(_0x3,true)
        }
      },
      immediate: false
    }
  },

  async mounted() {
    await this._0xM1()
    proj4.defs(_0xA2._s, _0xA2._s4)
    proj4.defs(_0xA2._p, _0xA2._p3)
    register(proj4)
    this.projection = getProjection(_0xA2._p)
    setTimeout(() => { this.initPreviewMap(this.center, this.zoom) }, 100)
    document.addEventListener('keydown', this.handleKeydown)
    document.addEventListener('click', this.handleOutsideClick)
    this._0xM2()
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('click', this.handleOutsideClick)
    if(this.previewMap){this.previewMap.dispose();this.previewMap=null}
    if(this.map){this.map.dispose();this.map=null}
    if(this.searchTimeout)clearTimeout(this.searchTimeout)
  },

  methods: {
    /* ── Panel / Active Map ── */
    setActivePanel(_0xP) { if(!this.$q.screen.gt.md){this.activePanel=this.activePanel===_0xP?null:_0xP} },
    getActiveMap() { return this.isFullscreen ? this.map : this.previewMap },
    retryLoad() { this.error=false;if(this.layerId)this.loadLayer(this.layerId,this.isFullscreen) },

    /* ── Init helpers (private) ── */
    async _0xM1() { this.dataLayers = await this.get(_0xA3._ti) },
    _0xM2() {
      if(typeof localStorage==='undefined')return
      const _0xS=localStorage.getItem('homeCoordinate')
      if(_0xS){try{const d=JSON.parse(_0xS);this.homeCoordinate=d.coordinate;this.homeData=d}catch(e){}}
    },

    /* ── Projection ── */
    async handleProjectionChange(_0xNP) {
      let _0xSC=this.getActiveMap()?.getView().getCenter(), _0xSZ=this.getActiveMap()?.getView().getZoom(), _0xCC, _0xCZ
      if(_0xSC){const ll=transform(_0xSC,this.projection,_0xA2._w);_0xCC=[ll[0],ll[1]]}
      this.projection=getProjection(_0xNP);this.currentProjection=_0xNP
      if(this.previewMap){this.previewMap.dispose();this.previewMap=null;this.currentDataLayer=null}
      if(this.map){this.map.dispose();this.map=null;this.fullscreenDataLayer=null}
      _0xCZ=_0xSZ||this.zoom;await this.$nextTick()
      if(!this.isFullscreen){await this.initPreviewMap(_0xCC,_0xCZ)}else{await this.initFullscreenMap(_0xCC,_0xCZ)}
    },

    handleFieldBoundariesToggle(_0xV) { this.fieldBoundariesVisible=_0xV;if(this.fieldLayer)this.fieldLayer.setVisible(_0xV) },

    /* ── Query Home Field ── */
    async queryHomeField(_0xQ) {
      if(!this.getActiveMap()||!this.homeData)return false
      const _0xQC=this.isFullscreen?this.$refs.queryComponentFullscreen:this.$refs.queryComponentPreview
      this.getActiveMap().getView().setZoom(15)
      const _0xHC=this.homeData.coordinate
      const _0xTC=transform(_0xHC,_0xA2._w,this.projection)
      this.getActiveMap().getView().setCenter(_0xTC)
      await new Promise(r=>setTimeout(r,300))
      this.highlightHomeField()
      const _0xSQ=this.cachedQuery&&this.cachedQuery.title===_0xQ.title&&this.cachedQuery.coords[0]===this.homeData.coordinate[0]&&this.cachedQuery.coords[1]===this.homeData.coordinate[1]
      if(_0xSQ){_0xQC.showCachedResult(this.cachedQuery.result);return}
      else{_0xQC.showHomeQuery(this.homeData,_0xQ)}
      this.queryHomeActive=true;this.queryHome=_0xQ
      return{coordinate:_0xHC,fieldData:this.homeData.fieldData}
    },

    handleQueryResult(_0xR) { this.cachedQuery={result:_0xR,title:this.queryHome?.title,coords:[...this.homeData.coordinate]} },

    /* ── Map Event Listeners ── */
    addMapEventListeners(_0xM) {
      _0xM.on('moveend',()=>{clearTimeout(this.moveTimeout);this.moveTimeout=setTimeout(()=>{this.loadFieldBoundaries()},300)})
      _0xM.getView().on('change:resolution',()=>{clearTimeout(this.zoomTimeout);this.zoomTimeout=setTimeout(()=>{this.loadFieldBoundaries()},500)})
    },

    /* ── Field Boundaries ── */
    _0xGE() { if(!this.getActiveMap())return null;return this.getActiveMap().getView().calculateExtent(this.getActiveMap().getSize()) },

    async _0xFB() {
      let _0xE=this._0xGE();if(!_0xE)return null
      try{
        this.fieldLoading=true
        const[a,b,c,d]=_0xE
        const _0xP={Srid:_0xB2(this.projection),Extent:`${a},${b},${c},${d}`,Zoom:this.map?Math.round(this.map.getView().getZoom()):_0xA1._mz}
        const _0xGD=await this.get(_0xA3._fb,_0xP)
        return _0xGD
      }catch(e){return null}finally{this.fieldLoading=false}
    },

    createFieldLayer() {
      if(!this.getActiveMap())return
      this.fieldBoundariesSource=new VectorSource({format:this.fieldBoundariesFormat})
      if(this.fieldLayer)this.getActiveMap().removeLayer(this.fieldLayer)
      const _0xFL=new VectorLayer({source:this.fieldBoundariesSource,style:this._0xFS,zIndex:100,name:_0xA3._fl})
      if(_0xFL){this.fieldLayer=_0xFL;this.getActiveMap().addLayer(this.fieldLayer);if(this.isFullscreen)this._0xFCI(this.getActiveMap());this.loadFieldBoundaries()}
      return _0xFL
    },

    _0xFS(_0xF) {
      const _0xCC=_0xF.get('color_code')||'#4CAF50'
      return new Style({fill:new Fill({color:_0xB1(_0xCC,0.3)}),stroke:new Stroke({color:_0xCC,width:1})})
    },

    _0xFCI(_0xM) {
      _0xM.on('singleclick',(e)=>{
        const _0xQC=this.isFullscreen?this.$refs.queryComponentFullscreen:this.$refs.queryComponentPreview
        if(_0xQC&&_0xQC.isOpen){if(this.lastSelectedFeature){this.lastSelectedFeature.setStyle(null);this.lastSelectedFeature=null};return}
        if(this.lastSelectedFeature){this.lastSelectedFeature.setStyle(null);this.lastSelectedFeature=null}
        const _0xFs=[];_0xM.forEachFeatureAtPixel(e.pixel,(f,l)=>{if(l===this.fieldLayer)_0xFs.push(f)})
        if(_0xFs.length>0){
          const _0xSF=_0xFs[0]
          const _0xCLL=transform(e.coordinate,this.projection,_0xA2._w)
          this.showFieldInfo(_0xSF,_0xCLL)
          _0xSF.setStyle(new Style({fill:new Fill({color:'rgba(255,255,0,0.8)'}),stroke:new Stroke({color:'#FF5722',width:3})}))
          this.lastSelectedFeature=_0xSF
        }
      })
    },

    applyHomeHighlight() {
      const _0xFs=this.fieldBoundariesSource?.getFeatures()||[]
      const _0xHF=_0xFs.find(f=>this._0xGFI(f)===this.homeData?.fieldData?.key)
      if(_0xHF){_0xHF.setStyle(new Style({fill:new Fill({color:'rgba(59,130,246,0.3)'}),stroke:new Stroke({color:'#3b82f6',width:3})}));this.lastSelectedFeature=_0xHF;this.homeLoading=false}
    },

    async highlightHomeField() {
      if(!this.homeData||!this.getActiveMap())return
      this.homeLoading=true
      if(this.lastSelectedFeature)this.lastSelectedFeature.setStyle(null)
      const _0xFs=this.fieldBoundariesSource.getFeatures()
      const _0xHF=_0xFs.find(f=>this._0xGFI(f)===this.homeData.fieldData?.key)
      if(!_0xHF)return
      this.applyHomeHighlight()
    },

    showFieldInfo(_0xF, _0xC) {
      const _0xP=_0xF.getProperties();const{geometry,..._0xFD}=_0xP
      this.selectedFieldData=_0xFD;this.selectedFieldCoordinate=_0xC;this.showFieldPopup=true
      this.$emit('field-selected',{properties:_0xP,coordinate:_0xC,feature:_0xF})
    },

    closeFieldPopup() { this.showFieldPopup=false;this.selectedFieldData=null;this.selectedFieldCoordinate=null },

    handleSetAsHome(_0xD) {
      this.homeCoordinate=_0xD.coordinate;this.homeData=_0xD
      if(typeof localStorage!=='undefined'){localStorage.setItem('homeCoordinate',JSON.stringify({coordinate:_0xD.coordinate,fieldData:_0xD.fieldData,timestamp:Date.now()}))}
      this.$emit('home-set',{coordinate:_0xD.coordinate,fieldData:_0xD.fieldData})
      this.closeFieldPopup()
    },

    _0xUFB(_0xGD) {
      const _0xNF=this.fieldBoundariesFormat.readFeatures(_0xGD)
      const _0xNI=new Set(),_0xNM=new Map()
      _0xNF.forEach(f=>{const id=this._0xGFI(f);if(id){_0xNI.add(id);_0xNM.set(id,f)}})
      const _0xEF=this.fieldBoundariesSource.getFeatures(),_0xEI=new Set()
      const _0xTR=[]
      _0xEF.forEach(f=>{const id=this._0xGFI(f);if(id){_0xEI.add(id);if(!_0xNI.has(id))_0xTR.push(f)}})
      _0xTR.forEach(f=>this.fieldBoundariesSource.removeFeature(f))
      const _0xTA=[]
      _0xNI.forEach(id=>{if(!_0xEI.has(id)){const f=_0xNM.get(id);if(f)_0xTA.push(f)}})
      if(_0xTA.length>0)this.fieldBoundariesSource.addFeatures(_0xTA)
    },

    _0xGFI(_0xF) { return _0xF.get('key') },

    async loadFieldBoundaries() {
      if(!this.getActiveMap())return
      const _0xZ=Math.round(this.getActiveMap().getView().getZoom())
      if(_0xZ<_0xA1._mz){this.fieldBoundariesSource.clear();return}
      const _0xGD=await this._0xFB()
      if(_0xGD&&_0xGD.features&&this.fieldBoundariesSource){this._0xUFB(_0xGD);if(this.homeLoading)this.applyHomeHighlight()}
    },

    /* ── Color / Style ── */
    getColorMap(_0xLCM, _0xLG) {
      let _0xCM
      if(_0xLCM){_0xCM=_0xLCM}
      else if(_0xLG){
        _0xCM='["match", ["band", 1]'
        for(let i=0;i<_0xLG.length;i++){if(_0xLG[i].active){_0xCM+=', '+_0xLG[i].from+', ['+_0xB1(_0xLG[i].color_code)+']'}}
        if(_0xCM==='["match", ["band", 1]')_0xCM+=', 0, [255, 255, 255, 0]'
        _0xCM+=', [255, 255, 255, 0]]'
      }
      return _0xCM
    },

    _0xRS(_0xV, _0xSF) {
      if(_0xSF.indexOf('x')===-1){_0xV=(_0xV/_0xSF)}else{try{let x=_0xV;_0xV=eval(_0xSF)}catch(e){}}
      return _0xV
    },

    parseColorMapToLegend(_0xCM, _0xSF) {
      let _0xLI=[]
      try{
        if(this.layerMetadata.attributes){
          Object.keys(this.layerMetadata.attributes).forEach(k=>{_0xLI.push({color_code:this.layerMetadata.attributes[k].color_code,text:this.layerMetadata.attributes[k].name,from:k,to:k,active:true})})
          return _0xLI.sort((a,b)=>a.text.localeCompare(b.text))
        }else if(_0xCM){
          const _0xII=_0xCM.findIndex(i=>i==='interpolate')
          if(_0xII===-1)return _0xB5()
          const _0xCP=[]
          for(let i=_0xII+3;i<_0xCM.length;i+=2){if(i+1<_0xCM.length){const v=_0xCM[i],c=_0xCM[i+1];if(typeof v==='number'&&Array.isArray(c)&&c.length>=3){_0xCP.push({value:Math.round((v/_0xSF)*100)/100,color:_0xB3(c)})}}}
          _0xLI=_0xCP.map((p,idx)=>{const np=_0xCP[idx+1];return{color_code:p.color,text:_0xB4(p.value,np?.value),value:p.value,from:p.value,to:np?.value||p.value,active:true}})
        }else{_0xLI=_0xB5()}
        return _0xLI
      }catch(e){return _0xB5()}
    },

    /* ── Year / Time ── */
    _0xEAY(_0xLF) {
      const _0xY=_0xLF.map(f=>_0xB7(f)).filter(y=>y!==null).sort((a,b)=>a-b)
      this.availableYears=[...new Set(_0xY)]
    },

    _0xFLY(_0xLF, _0xTY) { return _0xLF.find(f=>_0xB7(f)===parseInt(_0xTY)) },

    _0xGLDY(_0xY=null) {
      const _0xTY=_0xY||this.currentYear
      if(!this.allLayerFiles||this.allLayerFiles.length===0)return null
      let _0xSLF=this._0xFLY(this.allLayerFiles,_0xTY)
      if(!_0xSLF){_0xSLF=this.allLayerFiles[this.allLayerFiles.length-1]}
      this.layerTitle=this.layerMetadata.display_name?this.layerMetadata.display_name:_0xSLF.label||`Layer ${this.layerId}`
      return{..._0xSLF,...this.layerMetadata,source:_0xSLF.source}
    },

    async handleYearChange() {
      if(this.layerId){
        if(this.previewMap)await this.loadLayer(this.layerId,false,this.currentYear)
        if(this.map)await this.loadLayer(this.layerId,true,this.currentYear)
      }
    },

    /* ── Base Layer ── */
    changeBaseLayer(_0xLT, _0xTM=null) {
      const _0xM=_0xTM||this.previewMap||this.map;if(!_0xM)return
      const _0xLs=_0xM.getLayers().getArray()
      if(_0xLs[0]){_0xM.getLayers().setAt(0,this._0xCBL(_0xLT));this.$emit('base-layer-changed',{type:_0xLT,name:_0xLT})}
    },

    _0xCBL(_0xLT='osm') {
      const _0xBL={
        osm:()=>new TileLayer({source:new OSM(),name:'OpenStreetMap',className:'greyscale-layer',properties:{visible:true,zIndex:0},opacity:this.$q.dark.isActive?0.6:1.0}),
        satellite:()=>new TileLayer({source:new XYZ({url:'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',attributions:'© Esri, Maxar, GeoEye, Earthstar Geographics',maxZoom:19}),name:'Satellite',opacity:this.$q.dark.isActive?0.6:1.0})
      }
      return _0xBL[_0xLT]?_0xBL[_0xLT]():_0xBL.osm()
    },

    /* ── Layer Menu / Events ── */
    toggleLayerMenu() {
      if(!this.$q.screen.gt.md){this.setActivePanel('layers');this.layerMenuOpen=this.activePanel==='layers'}
      else{this.layerMenuOpen=!this.layerMenuOpen}
    },

    async handleLayerChange(_0xNL) { this.$emit('update:layerId',_0xNL);this.layerMenuOpen=false },
    handleBaseLayerChange(_0xLI) { this.selectedBaseLayer=_0xLI;if(this.previewMap)this.changeBaseLayer(_0xLI,this.previewMap);if(this.map)this.changeBaseLayer(_0xLI,this.map) },
    handleOpacityChange(_0xV) { this.currentOpacity=_0xV;if(this.currentDataLayer)this.currentDataLayer.setOpacity(_0xV);if(this.fullscreenDataLayer)this.fullscreenDataLayer.setOpacity(_0xV);this.$emit('opacity-changed',_0xV) },

    /* ── Fetch & Create Layer ── */
    async _0xFLD(_0xLI, _0xSY=null) {
      if(!_0xLI)return null
      try{
        this.loading=true;this.error=false
        const _0xLM=this.dataLayers?.find(l=>l.value===_0xLI)
        this.layerMetadata=_0xLM
        const _0xP={titleId:_0xLI,depth:this.effectiveDepth,confidence:_0xB8(this.effectiveConfidence),projection:this.projection.getCode()}
        const _0xR=await this.get(_0xA3._sf,_0xP)
        if(!_0xR||_0xR.length===0)throw new Error('No layer data received')
        this.allLayerFiles=_0xR;this._0xEAY(_0xR)
        let _0xLF
        if(_0xSY){_0xLF=this._0xFLY(_0xR,_0xSY)}
        else if(this.currentYear){_0xLF=this._0xFLY(_0xR,this.currentYear)}
        else{_0xLF=_0xR[0];this.currentYear=_0xB7(_0xLF)}
        this.layerTitle=this.layerMetadata.display_name?this.layerMetadata.display_name:_0xLF.label||`Layer ${_0xLI}`
        return{..._0xLF,..._0xLM,source:_0xLF.source}
      }catch(e){this.error=true;throw e}finally{this.loading=false}
    },

    async _0xCSL(_0xLD) {
      const _0xS=new GeoTIFFSource({sources:[{url:_0xLD.source,nodata:_0xLD.no_data||_0xA1._nd}],normalize:false,interpolate:false})
      let _0xCM=this.getColorMap(_0xLD.color_map,this.legendData),_0xLS
      if(_0xCM){try{_0xLS={color:JSON.parse(_0xCM)}}catch(e){_0xLS=_0xB6()}}else{_0xLS=_0xB6()}
      const _0xL=new WebGLTile({source:_0xS,opacity:this.currentOpacity,visible:true,style:_0xLS,zIndex:1})
      return{layer:_0xL,source:_0xS}
    },

    /* ── Init Maps ── */
    async initPreviewMap(_0xC=null, _0xZ=null) {
      if(!this.$refs.previewContainer)return
      const _0xBL=this._0xCBL(this.selectedBaseLayer)
      this.previewMap=new Map({
        target:this.$refs.previewContainer,layers:[_0xBL],
        view:new View({projection:this.projection,center:this.map?this.map.getView().getCenter():fromLonLat(_0xC||this.center,this.projection),zoom:this.map?this.map.getView().getZoom():_0xZ||this.zoom,interactions:[]}),
        controls:[new Attribution({collapsible:false,collapsed:false})]
      })
      this.addMapEventListeners(this.previewMap);await this.createFieldLayer(false)
      if(!this.$q.screen.gt.md)this.$refs.previewContainer.style.pointerEvents='none'
      this.$emit('map-ready',this.map)
      if(this.layerId)await this.loadLayer(this.layerId)
    },

    async initFullscreenMap(_0xC=null, _0xZ=null) {
      if(!this.$refs.fullscreenContainer)return
      const _0xBL=this._0xCBL(this.selectedBaseLayer)
      this.map=new Map({
        target:this.$refs.fullscreenContainer,layers:[_0xBL],
        view:new View({projection:this.projection,center:this.previewMap?this.previewMap.getView().getCenter():fromLonLat(_0xC||this.center,this.projection),zoom:this.previewMap?this.previewMap.getView().getZoom():_0xZ||this.zoom}),
        controls:[new Attribution({collapsible:false,collapsed:false})]
      })
      if(this.layerId)await this.loadLayer(this.layerId,true)
      this.addMapEventListeners(this.map);await this.createFieldLayer()
    },

    /* ── Load Layer ── */
    async loadLayer(_0xLI, _0xIF=false, _0xSY=null) {
      const _0xTM=_0xIF?this.map:this.previewMap;if(!_0xTM)return
      if(!_0xLI){this.previewMapLoaded=false;return}
      try{
        this.loading=true;this.error=false
        let _0xLD=null
        if(_0xSY&&this.layerMetadata!=null){_0xLD=this._0xGLDY(_0xSY)}
        else{_0xLD=await this._0xFLD(_0xLI)}
        const _0xEL=_0xIF?this.fullscreenDataLayer:this.currentDataLayer
        if(_0xEL&&_0xSY&&this.layerMetadata!=null){
          const _0xNS=new GeoTIFFSource({sources:[{url:_0xLD.source,nodata:_0xLD.no_data||_0xA1._nd}],normalize:false,interpolate:false})
          _0xEL.setSource(_0xNS);_0xEL.setOpacity(this.currentOpacity)
          this.$emit('layer-loaded',{layerId:_0xLI,layerData:_0xLD,layer:_0xEL});return
        }
        const _0xCL=_0xIF?this.fullscreenDataLayer:this.currentDataLayer
        if(_0xCL)_0xTM.removeLayer(_0xCL)
        this.legendData=this.parseColorMapToLegend(JSON.parse(_0xLD.color_map),_0xLD.scale_factor)
        const{layer:_0xNL}=await this._0xCSL(_0xLD)
        if(_0xIF){this.fullscreenDataLayer=_0xNL}else{this.currentDataLayer=_0xNL}
        _0xTM.addLayer(_0xNL)
        this.$emit('layer-loaded',{layerId:_0xLI,layerData:_0xLD,layer:_0xNL})
      }catch(e){this.previewMapLoaded=false;this.error=true;this.$emit('layer-error',{layerId:_0xLI,error:e.message})}
      finally{this.loading=false}
    },

    handleLegendItemSelected(_0xD) {},
    handleLegendToggled(_0xIO) { if(_0xIO)this.setActivePanel('legend') },

    /* ── Expand / Close ── */
    async handleExpand() {
      this.isFullscreen=true;await this.$nextTick()
      if(this.map){
        if(this.previewMap){const v=this.previewMap.getView();this.map.getView().setCenter(v.getCenter());this.map.getView().setZoom(v.getZoom())}
        this.map.setTarget(this.$refs.fullscreenContainer);this.map.updateSize()
        if(this.fieldLayer){try{this.previewMap?.removeLayer(this.fieldLayer)}catch(e){};if(!this.map.getLayers().getArray().includes(this.fieldLayer))this.map.addLayer(this.fieldLayer)}
      }else{await this.initFullscreenMap()}
      if(this.queryHomeActive){this.queryHomeActive=false;this.queryHomeField(this.queryHome)}
      this.$emit('expand',{layerId:this.layerId,layerTitle:this.layerTitle})
    },

    async handleClose() {
      let _0xLC=null,_0xLZ=null
      if(this.map){const v=this.map.getView();_0xLC=v.getCenter();_0xLZ=v.getZoom()}
      this.isFullscreen=false;this.layerMenuOpen=false;this.$emit('close');await this.$nextTick()
      if(this.previewMap){
        this.previewMap.setTarget(this.$refs.previewContainer)
        if(_0xLC&&_0xLZ){this.previewMap.getView().setCenter(_0xLC);this.previewMap.getView().setZoom(_0xLZ)}
        this.previewMap.updateSize()
        if(this.fieldLayer){try{this.map?.removeLayer(this.fieldLayer)}catch(e){};if(!this.previewMap.getLayers().getArray().includes(this.fieldLayer))this.previewMap.addLayer(this.fieldLayer)}
      }
    },

    handleKeydown(_0xE) { if(this.isFullscreen){if(_0xE.key==='Escape'){if(this.layerMenuOpen){this.layerMenuOpen=false}else{this.handleClose()}}} },
    handleLocationSelected(_0xD) {},
    handleSearchOpened() {},
    handleSearchClosed() {},

    /* ── Public API ── */
    getCenter() { return this.map?.getView().getCenter() },
    getZoom() { return this.map?.getView().getZoom() },
    setCenter(_0xC) { this.map?.getView().setCenter(fromLonLat(_0xC,this.projection)) },
    setZoom(_0xZ) { this.map?.getView().setZoom(_0xZ) },
    getCurrentLayer() { return{id:this.layerId,title:this.layerTitle} }
  },

  computed: {
    layerInfo() { return this.layerTitle+(this.currentYear?` (${this.currentYear})`:'') },
    availableDepths() { const d=this.layerMetadata?.depth_list;if(!d||d==='s')return['s'];return d.split(', ').map(x=>x.trim()) },
    availableConfidences() { const c=this.layerMetadata?.confidence_list;if(!c)return[''];return c.split(', ').map(x=>x.trim()) },
    effectiveDepth() { return this.availableDepths[0]||'s' },
    effectiveConfidence() { return this.availableConfidences[0] },
    currentLayerData() { return this.fullscreenDataLayer||this.currentDataLayer },
    hasLegendData() { return this.legendData&&this.legendData.length>0 },
    showLegend() { return this.hasLegendData&&!this.legendLoading&&this.isFullscreen },
    legendTitle() { return this.layerTitle||this.currentLayerData?.label||'Layer Legend' },
    legendLayerInfo() {
      if(!this.layerMetadata)return{name:'Legend',year:null,description:null,unit:symOutlinedNotificationMultiple}
      return{name:this.layerMetadata.name,year:this.currentYear,description:this.layerMetadata.description,unit:this.layerMetadata.unit}
    }
  }
}
</script>

<style lang="scss">
.greyscale-layer { filter: grayscale(80%); }
</style>

<style lang="scss" scoped>
.map-preview{display:flex;margin:0 1.25rem;border-radius:var(--radius-xl,1rem);overflow:hidden;position:relative;flex:1;min-height:12.5rem;cursor:pointer;background:$grey-2;body.body--dark &{background:$dark-page}}
.preview-map-container{position:relative;width:100%;flex:1;border-radius:var(--radius-lg,1rem);:deep(.ol-control){display:none !important}}
.map-overlay{position:absolute;inset:0;pointer-events:none;display:flex;align-items:flex-end;justify-content:center;padding:1rem}
.expand-btn{background:rgba(255,255,255,0.95);border:none;padding:0.5rem 1rem;border-radius:1.25rem;font-size:0.75rem;color:$grey-8;cursor:pointer;transition:all 0.3s ease;pointer-events:auto;backdrop-filter:blur(5px);box-shadow:var(--shadow-md);&:hover{background:white;transform:scale(1.05);box-shadow:0 4px 12px rgba(0,0,0,0.2)}body.body--dark &{background:rgba(30,30,30,0.95);color:white;&:hover{background:$dark}}}
.fullscreen-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.8);z-index:9999;display:flex;align-items:center;justify-content:center;animation:fadeIn 0.3s ease;body.body--dark &{background:rgba(0,0,0,0.95)}}
.fullscreen-container{width:100vw;height:100dvh;background:white;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.3);animation:slideUp 0.3s ease;body.body--dark &{background:$dark-page}}
.panels-container{}
@keyframes fadeIn{from{opacity:0}to{opacity:1}}
@keyframes slideUp{from{opacity:0;transform:translateY(1.25rem) scale(0.95)}to{opacity:1;transform:translateY(0) scale(1)}}
.fullscreen-header{display:flex;justify-content:space-between;align-items:center;padding:0.875rem 1.5rem;background:rgba(255,255,255,0.3);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);z-index:10;position:relative;color:$grey-8;@media(min-width:48rem){background:transparent;backdrop-filter:none;-webkit-backdrop-filter:none}body.body--dark &{color:$grey-5;@media(max-width:48rem),(max-height:48rem){background:rgba(30,30,30,0.3)}}}
.header-left{display:flex;align-items:center;gap:0.75rem;@media(min-width:48rem){background:rgba(255,255,255,0.3);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);padding:0.5rem 0.75rem;padding-right:3rem;padding-left:1.5rem;margin-top:-0.875rem;margin-left:-1.5rem;border:1px solid rgba(255,255,255,0.2);mask-image:linear-gradient(to right,rgba(0,0,0,1) 0%,rgba(0,0,0,1) 65%,rgba(0,0,0,0) 100%);body.body--dark &{background:rgba(30,30,30,0.3)}}}
.header-title{font-size:1.1rem;font-weight:500;color:$grey-8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:12rem;body.body--dark &{color:$grey-3}@media(max-width:48rem),(max-height:48rem){max-width:8rem;font-size:1rem}}
.header-right{display:flex;align-items:center;flex-direction:row-reverse;gap:1rem}
.map-controls-overlay{position:absolute;bottom:var(--map-ui-bottom);right:var(--map-ui-right);z-index:var(--z-map-ui);@media(max-width:48rem),(max-height:48rem){bottom:var(--map-ui-bottom-mobile,1rem);right:var(--map-ui-right-mobile,1rem)}}
.fullscreen-map-container{position:absolute;inset:0}
.fullscreen-map{width:100%;height:100%}
.retry-btn{margin-left:0.75rem;padding:0.375rem 0.75rem;background:$info;color:white;border:none;border-radius:var(--radius-sm);cursor:pointer;font-size:0.875rem;transition:background 0.2s;&:hover{filter:brightness(0.9)}}
@media(max-width:48rem),(max-height:48rem){.fullscreen-container{width:100vw;border-radius:0}.fullscreen-header{padding:0.5rem 0.375rem;gap:0.75rem;align-items:stretch}.header-left{gap:0.25rem;justify-content:space-between;align-items:center;display:flex}.header-right{justify-content:space-between}}
</style>